import { useMemo, useState, type FormEvent } from "react";
import {
  createPageDomain,
  createNameserverTransferPlan,
  usePageDomains,
  type PageDomainRecord,
  type NameserverTransferPlan,
  type NameserverTransferRecordInput,
} from "@/hooks/use-page-domains";

function normalizeDomainInput(value: string): string {
  return value
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/.*$/, "")
    .replace(/\.$/, "")
    .toLowerCase();
}

function statusLabel(domain: PageDomainRecord): string {
  return `${domain.status} / SSL ${domain.sslStatus ?? "pending"}`;
}

const EXAMPLE_RECORDS_JSON = JSON.stringify([
  { type: "A", name: "@", content: "192.0.2.10", ttl: 300 },
  { type: "MX", name: "@", content: "mail.example.com", ttl: 300, priority: 10 },
  { type: "TXT", name: "@", content: "v=spf1 include:_spf.example.com ~all", ttl: 300 },
], null, 2);

function parseNameserverList(value: string): string[] {
  return value
    .split(/[,\s]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export default function DomainPurchasePage() {
  const { domains, isLoading, error, refetch } = usePageDomains();
  const [domainInput, setDomainInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [createdDomain, setCreatedDomain] = useState<PageDomainRecord | null>(null);
  const [cnameTarget, setCnameTarget] = useState<string | null>(null);
  const [transferDomainId, setTransferDomainId] = useState("");
  const [registrar, setRegistrar] = useState("");
  const [nameserversInput, setNameserversInput] = useState("");
  const [recordsJson, setRecordsJson] = useState(EXAMPLE_RECORDS_JSON);
  const [transferPlan, setTransferPlan] = useState<NameserverTransferPlan | null>(null);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [isPlanningTransfer, setIsPlanningTransfer] = useState(false);

  const orderedDomains = useMemo(
    () => [...domains].sort((left, right) => left.domain.localeCompare(right.domain)),
    [domains],
  );
  const selectedTransferDomainId = transferDomainId || orderedDomains[0]?.id || "";

  async function handleCreateDomain(event: FormEvent) {
    event.preventDefault();

    const domain = normalizeDomainInput(domainInput);
    if (!domain) {
      return;
    }

    setIsSubmitting(true);
    setActionError(null);
    setCreatedDomain(null);
    setCnameTarget(null);

    try {
      const result = await createPageDomain(domain);
      setCreatedDomain(result.domain);
      setCnameTarget(result.cnameTarget);
      setDomainInput("");
      await refetch();
    } catch {
      setActionError("Failed to create the mounted domain setup record.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCreateTransferPlan(event: FormEvent) {
    event.preventDefault();
    if (!selectedTransferDomainId) {
      return;
    }

    setTransferError(null);
    setTransferPlan(null);
    setIsPlanningTransfer(true);

    try {
      const parsed = recordsJson.trim() ? JSON.parse(recordsJson) as unknown : [];
      if (!Array.isArray(parsed)) {
        throw new Error("DNS records must be a JSON array.");
      }

      const plan = await createNameserverTransferPlan(selectedTransferDomainId, {
        registrar: registrar.trim() || undefined,
        assignedCloudflareNameservers: parseNameserverList(nameserversInput),
        existingRecords: parsed as NameserverTransferRecordInput[],
      });
      setTransferPlan(plan);
    } catch (planError) {
      setTransferError(planError instanceof Error ? planError.message : "Failed to build nameserver transfer plan.");
    } finally {
      setIsPlanningTransfer(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-6">
      <section className="rounded-3xl border border-border bg-[linear-gradient(135deg,rgba(16,64,204,0.08),rgba(15,23,42,0.02))] p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary">Mounted domain setup</p>
        <h2 className="mt-2 text-2xl font-semibold text-foreground">Create a custom-domain setup</h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          This route now uses the mounted `/crm/pages/domains` lifecycle. It creates the domain setup record, returns the canonical CNAME target, and hands the domain to the existing DNS verification flow.
        </p>
      </section>

      {error ? (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {actionError ? (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-700">
          {actionError}
        </div>
      ) : null}

      <section className="rounded-3xl border border-border bg-card p-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Register the domain setup in Stero</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Use a domain you already own or plan to register externally. Direct Cloudflare Registrar checkout is not mounted yet, so this page no longer shows fake availability or prices.
          </p>
        </div>

        <form onSubmit={handleCreateDomain} className="mt-5 space-y-4">
          <div>
            <label htmlFor="domain-setup-domain" className="block text-sm font-medium text-foreground">
              Domain name
            </label>
            <input
              id="domain-setup-domain"
              type="text"
              value={domainInput}
              onChange={(event) => setDomainInput(event.target.value)}
              placeholder="www.mybusiness.com"
              required
              className="mt-1 w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !domainInput.trim()}
            className="rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {isSubmitting ? "Creating setup..." : "Create domain setup"}
          </button>
        </form>
      </section>

      {createdDomain && cnameTarget ? (
        <section className="rounded-3xl border border-emerald-500/20 bg-emerald-500/5 p-6">
          <p className="text-sm font-semibold text-foreground">Mounted setup created for {createdDomain.domain}</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Point the domain CNAME record at <span className="font-mono text-foreground">{cnameTarget}</span>, then verify DNS from the Custom Domains page.
          </p>
          <a
            href="/settings/domains"
            className="mt-4 inline-flex rounded-full border border-emerald-500/30 px-4 py-2 text-sm font-medium text-foreground hover:bg-background"
          >
            Open custom domains
          </a>
        </section>
      ) : null}

      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-sm font-semibold text-foreground">Mounted now</p>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li>Custom-domain setup records through `/crm/pages/domains`.</li>
            <li>Canonical CNAME target returned by the backend.</li>
            <li>DNS scan, verification, SSL status, and removal in Custom Domains.</li>
            <li>Operator-assisted nameserver transfer planning and DNS import preflight.</li>
          </ul>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <p className="text-sm font-semibold text-amber-900">Not mounted yet</p>
          <ul className="mt-3 space-y-2 text-sm text-amber-900">
            <li>Cloudflare Registrar availability search.</li>
            <li>Registrar checkout, WHOIS/privacy billing, and renewal automation.</li>
            <li>Live Cloudflare zone creation, DNS import execution, and NS polling automation.</li>
          </ul>
        </div>
      </section>

      <section className="rounded-3xl border border-border bg-card p-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Nameserver transfer preflight</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Build a mounted operator plan before pointing nameservers to Cloudflare. This validates the DNS import list and keeps MX/TXT records visible before cutover.
          </p>
        </div>

        <form onSubmit={handleCreateTransferPlan} className="mt-5 space-y-4">
          <div>
            <label htmlFor="transfer-domain" className="block text-sm font-medium text-foreground">
              Domain setup
            </label>
            <select
              id="transfer-domain"
              value={selectedTransferDomainId}
              onChange={(event) => setTransferDomainId(event.target.value)}
              disabled={orderedDomains.length === 0}
              className="mt-1 w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary"
            >
              {orderedDomains.length === 0 ? (
                <option value="">Create a domain setup first</option>
              ) : orderedDomains.map((domain) => (
                <option key={domain.id} value={domain.id}>{domain.domain}</option>
              ))}
            </select>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm font-medium text-foreground">
              Current registrar
              <input
                value={registrar}
                onChange={(event) => setRegistrar(event.target.value)}
                placeholder="Namecheap, GoDaddy, Cloudflare Registrar..."
                className="mt-1 w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary"
              />
            </label>

            <label className="block text-sm font-medium text-foreground">
              Assigned Cloudflare nameservers
              <input
                value={nameserversInput}
                onChange={(event) => setNameserversInput(event.target.value)}
                placeholder="ada.ns.cloudflare.com bob.ns.cloudflare.com"
                className="mt-1 w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary"
              />
            </label>
          </div>

          <label className="block text-sm font-medium text-foreground">
            Existing DNS records to import
            <textarea
              value={recordsJson}
              onChange={(event) => setRecordsJson(event.target.value)}
              rows={8}
              className="mt-1 w-full rounded-2xl border border-input bg-background px-4 py-3 font-mono text-xs text-foreground outline-none transition focus:border-primary"
            />
          </label>

          {transferError ? (
            <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-700">
              {transferError}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={isPlanningTransfer || !selectedTransferDomainId}
            className="rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {isPlanningTransfer ? "Building plan..." : "Build nameserver transfer plan"}
          </button>
        </form>

        {transferPlan ? (
          <div className="mt-5 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5">
            <p className="text-sm font-semibold text-foreground">Transfer plan ready for {transferPlan.domain}</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Importable records: {transferPlan.importPlan.importableRecords.length} / {transferPlan.importPlan.totalInputRecords}. Skipped records: {transferPlan.importPlan.skippedRecords.length}.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Cloudflare nameservers: {transferPlan.cloudflareNameservers.length > 0 ? transferPlan.cloudflareNameservers.join(", ") : "create the Cloudflare zone first, then paste assigned nameservers."}
            </p>
            <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
              {transferPlan.zeroDowntimeChecklist.map((item) => <li key={item}>{item}</li>)}
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              Automation status: zone create {String(transferPlan.automationStatus.zoneCreateMounted)}, DNS import {String(transferPlan.automationStatus.dnsImportMounted)}, NS verification {String(transferPlan.automationStatus.nsVerificationMounted)}.
            </p>
          </div>
        ) : null}
      </section>

      <section className="rounded-3xl border border-border bg-card p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Existing mounted domains</h3>
            <p className="text-sm text-muted-foreground">These are the domains currently returned by the live custom-domain contract.</p>
          </div>
          <a href="/settings/domains" className="rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent">
            Manage DNS
          </a>
        </div>

        {isLoading ? (
          <p className="mt-5 text-sm text-muted-foreground">Loading mounted domains...</p>
        ) : orderedDomains.length === 0 ? (
          <p className="mt-5 rounded-2xl border border-dashed border-border p-5 text-sm text-muted-foreground">
            No custom domains are registered yet.
          </p>
        ) : (
          <div className="mt-5 divide-y divide-border rounded-2xl border border-border">
            {orderedDomains.map((domain) => (
              <div key={domain.id} className="flex flex-col gap-1 p-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-medium text-foreground">{domain.domain}</p>
                <p className="text-sm text-muted-foreground">{statusLabel(domain)}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
